"use client";

import { FC, ReactNode, useEffect, useRef, useState } from "react";
import useFcmToken from "@shared/src/hooks/useFcmToken";
import protectedAxios from "@shared/src/utils/axios";
import { useNotificationStore } from "@shared/src/stores/useNotificationStore";
import { orderService } from "@shared/src/services/order.service";
import { addOrderToInProgressQueries, updateOrderInQueries } from "@shared/src/lib/cache";
import { ixToast } from "@shared/src/utils/ixToast";
import { ORDER_PAGE } from "@shared/src/config/page-links";
import { Unsubscribe, onMessage } from "@firebase/messaging";
import { messagingInstance } from "@shared/src/config/firebase";
import { ParsedOrderJson, ParsedChatJson } from "@sdk/notification/types";
import { useHostStore } from "@shared/src/stores/useHostStore";

type ParseNotificationResult = ParsedChatJson | ParsedOrderJson;
type RawNotification = {
  id?: string;
  data?: string | { json?: string };
  messageId?: string;
  addressId?: string;
  title?: string;
  body?: string;
  notification?: { title?: string; body?: string };
  red?: boolean;
};

export function parseNotificationData(
  raw: RawNotification,
  noteType: "order" | "chat",
): ParseNotificationResult | null {
  try {
    const json =
      typeof raw.data === "string"
        ? JSON.parse(raw.data)
        : typeof raw.data?.json === "string"
        ? JSON.parse(raw.data.json)
        : {};

    const createdAt = json.createdAt ?? new Date().toISOString();
    const id = raw.id ?? crypto.randomUUID();

    if (noteType === "chat") {
      return {
        id: json.id,
        text: json.text,
        attachments: json.attachments || [],
        sender: json.sender,
        receiver: json.receiver,
        orderId: json.orderId,
        createdAt,
      };
    }

    return {
      id,
      from: "776258925896",
      messageId: raw.messageId,
      addressId: raw.addressId,
      title: raw.title ?? raw.notification?.title ?? noteType.toUpperCase(),
      body: raw.body ?? raw.notification?.body ?? json.text ?? "",
      note: noteType,
      orderHash: json.orderId ?? json.orderHash ?? "",
      state: json.state ?? "",
      red: !!raw.red,
      createdAt: new Date(createdAt),
      quickTradeId: json.quickTradeId ?? "",
    };
  } catch (err) {
    console.error("❌ Failed to parse notification data:", err);
    return null;
  }
}

const refetchOrderNotifications = async (excludeIds?: string[]) => {
  const isAuthenticated = useHostStore.getState().sdk?.auth.status === "authenticated";
  if (!isAuthenticated) return;

  try {
    const { data } = await protectedAxios.get<ApiNotification[]>("/auth/notifications");
    const latestNotifications = data.slice(0, 10);
    console.log("fetch notify: ", latestNotifications);

    for (const notif of latestNotifications.reverse()) {
      if (excludeIds?.includes(notif.id)) continue;

      const parsed = parseNotificationData({ ...notif, data: notif.data }, notif.note === "chat" ? "chat" : "order");

      if (!parsed) continue;

      if (notif.note === "chat" && "text" in parsed) {
        useNotificationStore.getState().addChatNotification(parsed);
      } else if (notif.note === "order" && "orderHash" in parsed) {
        await useNotificationStore.getState().addOrderNotification(parsed);
      }
    }
  } catch (error) {
    console.error("Failed to fetch notifications:", error);
  }
};

export const markNotificationsAsRead = async (ids: string[]) => {
  console.log(ids);
  if (!ids?.length) return;

  try {
    await protectedAxios.post(`/auth/notifications/read`, { ids });
    useNotificationStore.getState().removeOrderNotificationsByIds(ids);

    // await new Promise(resolve => setTimeout(resolve, 1000));
    // await refetchOrderNotifications(ids);
  } catch (error) {
    console.error("Failed to mark notifications as read", error);
    throw error;
  }
};

const handleNotification = async (raw: any, noteType: "order" | "chat") => {
  const router = useHostStore.getState().router;
  console.log("🔔 websocket handleNotification", raw);
  const parsed = parseNotificationData(raw, noteType);
  if (!parsed) return;

  if (noteType === "chat" && "text" in parsed) {
    useNotificationStore.getState().addChatNotification(parsed);
    ixToast.success(parsed.text, {
      onClick: () => router.push(ORDER_PAGE(parsed.orderId)),
    });
    return;
  }

  if (noteType === "order" && "orderHash" in parsed) {
    const flat = parsed as ParsedOrderJson;
    const exists = useNotificationStore.getState().orderNotifications.find(n => n.id === flat.id);
    if (exists) return;

    try {
      const order = await orderService.getOrder(flat.orderHash);
      if (order?.data) {
        addOrderToInProgressQueries(order.data);
        updateOrderInQueries({ newOrder: order.data });
      }
    } catch (err) {
      console.error("⚠️ Failed to fetch order:", err);
    }

    await useNotificationStore.getState().addOrderNotification(flat);
    ixToast.success(flat.body, {
      onClick: () => router.push(ORDER_PAGE(flat.orderHash)),
    });
  }
};

interface NotificationProviderProps {
  children: ReactNode;
}

interface ApiNotification {
  id: string;
  addressId: string;
  title: string;
  body: string;
  data: string;
  note: string;
  createdAt: string;
  red: boolean;
}

export const NotificationProvider: FC<NotificationProviderProps> = ({ children }) => {
  const { token, notificationPermissionStatus } = useFcmToken();
  const sdk = useHostStore(state => state.sdk);
  const isWebsocketActive = useNotificationStore(state => state.isWebsocketActive);
  const setIsWebsocketActive = useNotificationStore(state => state.setIsWebsocketActive);
  const [isAuthenticated, setIsAuthenticated] = useState(sdk?.auth.status === "authenticated");
  const activeAccountAddr = sdk?.auth.getAddress();

  const hasRegisteredToken = useRef(false);
  const unsubscribeRef = useRef<Unsubscribe | null>(null);

  useEffect(() => {
    const unsubscribe = sdk?.subscribe("AUTHENTICATED", evt => {
      console.log("🔔 sdk event in notification provider", evt);
      refetchOrderNotifications();
      setIsAuthenticated(true);
    });
    return () => unsubscribe?.();
  }, [sdk]);

  // Register FCM Token
  useEffect(() => {
    const registerToken = async (attempt = 1) => {
      if (!token || !isAuthenticated || hasRegisteredToken.current) return;
      try {
        await protectedAxios.post("/auth/notification/register", { token });
        hasRegisteredToken.current = true;
        console.log("✅ Token registered");
      } catch (err) {
        console.warn(`❌ Token registration failed (Attempt ${attempt})`, err);
        if (attempt < 3) setTimeout(() => registerToken(attempt + 1), 3000);
      }
    };
    registerToken();
  }, [token, isAuthenticated]);

  // FCM Foreground
  useEffect(() => {
    if (!("Notification" in window) || Notification.permission !== "granted" || !token) return;

    const setupFcm = async () => {
      const messaging = await messagingInstance();
      if (!messaging) return;

      const unsubscribe = onMessage(messaging, async payload => {
        await handleNotification(payload, payload.data?.note === "chat" ? "chat" : "order");
      });

      unsubscribeRef.current = unsubscribe;
    };

    setupFcm();
    return () => {
      if (unsubscribeRef.current) unsubscribeRef.current();
    };
  }, [token]);

  // WebSocket
  useEffect(() => {
    console.log("🔔 websocket Notification permission status:", notificationPermissionStatus);
    if (notificationPermissionStatus === "granted" || !isAuthenticated || !activeAccountAddr) return;

    const setupWebSocket = () => {
      console.log("🔔 setupWebSocket");
      if (isWebsocketActive) {
        const state = sdk?.notification.getState();
        console.log("🟡 WebSocket already active with state:", state);
        if (state === "connected" || state === "connecting") return;
      }

      const unsubscribeWebSocketConnected = sdk?.subscribe("WEBSOCKET_CONNECTED", data => {
        console.log("🔔 WebSocket connected in provider", data);
        setIsWebsocketActive(true);
      });
      const unsubscribeWebSocketDisconnected = sdk?.subscribe("WEBSOCKET_DISCONNECTED", data => {
        console.log("🔔 WebSocket disconnected in provider", data);
        setIsWebsocketActive(false);
      });

      const unsubscribeChat = sdk?.subscribe("CHAT", data => handleNotification(data.data, "chat"));
      const unsubscribeOrder = sdk?.subscribe("ORDER", data => handleNotification(data.data, "order"));

      return () => {
        unsubscribeWebSocketConnected?.();
        unsubscribeWebSocketDisconnected?.();
        unsubscribeChat?.();
        unsubscribeOrder?.();
        sdk?.notification.cleanup();
      };
    };

    const unsubscribe = setupWebSocket();

    return () => {
      unsubscribe?.();
    };
  }, [notificationPermissionStatus, isAuthenticated, activeAccountAddr]);

  return <>{children}</>;
};
